import React, { useState, useEffect } from "react";
import { Table, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { TableProps } from "antd/es/table";
import "antd/dist/reset.css";
import ImageWithBasePath from "../../../../../core/common/imageWithBasePath";
import {
  formatAvgDuration,
  formatDuration,
  formatNumber,
  formatPercentage,
  formatTime,
} from "../../helpers/numbers.helper";

interface AntdDataTableProps {
  data: any[];
  columnKey: string | any;
  loading?: boolean;
  error?: string;
  height?: number;
  showPagination?: boolean;
  pageSize?: number;
  rowKey?: string;
  showSearch?: boolean;
}

const AntdDataTable: React.FC<AntdDataTableProps> = ({
  data,
  columnKey,
  loading = false,
  error,
  showPagination = true,
  pageSize = 4,
  rowKey = "key",
  showSearch = true,
}) => {
  //Search state
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState(data);

  const nameColumnWidth = 200;
  const valueColumnWidth = 100;

  //Handle search
  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!value) {
      setFilteredData(data);
      return;
    }
    const searchLower = value.toLowerCase();
    const filtered = data.filter((record) => {
      return Object.values(record).some((field) => {
        if (field === null || field === undefined) return false;
        return String(field).toLowerCase().includes(searchLower);
      });
    });

    setFilteredData(filtered);
  };

  useEffect(() => {
    setFilteredData(data);
    setSearchText("");
  }, [data]);

  if (error) {
    return (
      <div className="table-error-state">
        <i className="ti ti-alert-circle text-danger" />
        <p className="text-danger">Error loading data</p>
        <small className="text-muted">{error}</small>
      </div>
    );
  }

  const calls_columns = [
    {
      title: "Employee",
      dataIndex: "user_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-md avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.profile_pic || record?.profile_pic === "null"
                  ? "assets/img/users/user.png"
                  : record?.profile_pic
              }
              alt="img"
            />
          </li>
          <div className="">
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">{text}</h6>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.user_name || "").localeCompare(b?.user_name || ""),
      width: nameColumnWidth,
    },
    {
      title: "Total Calls",
      dataIndex: "total_calls",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.total_calls - b?.total_calls,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Connected Calls",
      dataIndex: "connected_calls",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-success-transparent py-2 p-2">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.connected_calls - b?.connected_calls,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Missed Calls",
      dataIndex: "missed_calls",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-danger-transparent py-2 p-2">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.missed_calls - b?.missed_calls,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Total Duration",
      dataIndex: "total_call_duration",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2">
          {formatDuration(text)}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        a?.total_call_duration - b?.total_call_duration,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Avg Duration",
      dataIndex: "avg_duration",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2">
          {formatAvgDuration(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.avg_duration - b?.avg_duration,
      align: "center",
      width: valueColumnWidth,
    },
  ];

  const attendance_columns = [
    {
      title: "Employee",
      dataIndex: "user_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-md avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.profile_pic || record?.profile_pic === "null"
                  ? "assets/img/users/user.png"
                  : record?.profile_pic
              }
              alt="img"
            />
          </li>
          <div className="">
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">{text}</h6>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.user_name || "").localeCompare(b?.user_name || ""),
      width: nameColumnWidth,
    },
    {
      title: "Present Count",
      dataIndex: "present_count",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-success-transparent py-2 p-2">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.present_count - b?.present_count,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Absent Count",
      dataIndex: "absent_count",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-danger-transparent py-2 p-2">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.absent_count - b?.absent_count,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Late Count",
      dataIndex: "late_count",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-warning-transparent py-2 p-2">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.late_count - b?.late_count,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Total Working Hours",
      dataIndex: "total_working_hours",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2">
          {formatDuration(text)}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        a?.total_working_hours - b?.total_working_hours,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Avg Working Hours",
      dataIndex: "avg_working_hours",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2">
          {formatAvgDuration(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.avg_working_hours - b?.avg_working_hours,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Avg Checkin Time",
      dataIndex: "avg_checkin_time",
      render: (text: string | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2">
          {formatTime(text)}
        </h6>
      ),
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Avg Checkout Time",
      dataIndex: "avg_checkout_time",
      render: (text: string | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2">
          {formatTime(text)}
        </h6>
      ),
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Total Late Time",
      dataIndex: "total_late_seconds",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2">
          {formatDuration(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.total_late_seconds - b?.total_late_seconds,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Avg Late Time",
      dataIndex: "avg_late_time",
      render: (text: string | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-danger-transparent py-2 p-2">
          {formatAvgDuration(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.avg_late_time - b?.avg_late_time,
      align: "center",
      width: valueColumnWidth,
    },
  ];

  const lead_performer_columns = [
    {
      title: "Employee",
      dataIndex: "employee_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-md avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.employee_profile || record?.employee_profile === "null"
                  ? "assets/img/users/user.png"
                  : record?.employee_profile
              }
              alt="img"
            />
          </li>
          <div className="">
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">{text}</h6>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a?.employee_name || "").localeCompare(b?.employee_name || ""),
      width: nameColumnWidth,
    },
    {
      title: "Assigned",
      dataIndex: "total_assigned_leads",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 p-2">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        a?.total_assigned_leads - b?.total_assigned_leads,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Converted",
      dataIndex: "converted_leads",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-success-transparent py-2 p-2">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.converted_leads - b?.converted_leads,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Lost",
      dataIndex: "lost_leads",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-danger-transparent py-2 p-2">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.lost_leads - b?.lost_leads,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "In Process",
      dataIndex: "in_progress_leads",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-purple-transparent py-2 p-2">
          {formatNumber(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.in_progress_leads - b?.in_progress_leads,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Conversion",
      dataIndex: "conversion_rate",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2">
          {formatPercentage(text)}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.conversion_rate - b?.conversion_rate,
      align: "center",
      width: valueColumnWidth,
    },
    {
      title: "Ranking",
      dataIndex: "performance_rank",
      render: (text: number | any) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-primary ">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.performance_rank - b?.performance_rank,
      align: "center",
      width: valueColumnWidth,
    },
  ];

  let columns: any = [];

  switch (columnKey) {
    case "calls_columns":
      columns = calls_columns;
      break;
    case "attendance_columns":
      columns = attendance_columns;
      break;
    case "lead_performer_columns":
      columns = lead_performer_columns;
      break;
    default:
      columns = [];
      break;
  }

  const tableProps: TableProps<any> = {
    columns,
    dataSource: filteredData,
    loading,
    rowKey,
    // scroll: { y: "auto", x: "auto" },
    scroll: {
      y: "auto",
      x: "max-content",
    },
    pagination: showPagination
      ? {
          pageSize,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }
      : false,
  };

  return (
    <div style={{ width: "100%" }}>
      {/* Search Input */}
      {showSearch && (
        <div style={{ marginBottom: 10 }}>
          <Input
            placeholder="Search across all columns..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ maxWidth: 250 }}
          />
          {searchText && (
            <span style={{ marginLeft: 5, color: "#888", fontSize: 14 }}>
              Showing {filteredData.length} of {data.length} records
            </span>
          )}
        </div>
      )}

      <Table {...tableProps} />
    </div>
  );
};

export default AntdDataTable;
